import { useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { coreGet } from "@/services/coreApi";
import { nowUtc } from "@/utils/appNow";
import { useAccessContext } from "../contexts/AccessContext";

dayjs.extend(utc);

/**
 * Fetches reported/planned waypoints for the window [fromPicker, now].
 *
 * The window end is "now" (recomputed at fetch time) — reported data is past
 * track, decoupled from the time-slider's right edge. Re-fetches whenever
 * `fromPicker` (the history start) changes, `vesselId`/`enabled` change, or
 * `refreshTick` is bumped (e.g. after a route import).
 *
 * When `enabled` is false, nothing is fetched (the cleared result is returned).
 * When `vesselId` is set, only that vessel is fetched; when null, all vessels in
 * scope are fetched.
 */
export const useFetchWaypointsGeojson = (
  fromPicker,
  routeType,
  refreshTick = 0,
  vesselId = null,
  enabled = true,
) => {
  const { accessContext, setAccessContext } = useAccessContext();

  const [meteotrackerWaypointsGeojson, setmeteotrackerWaypointsGeojson] =
    useState(null);
  const [loadingStates, setLoadingStates] = useState({
    reported: false,
    planned: false,
  });

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setmeteotrackerWaypointsGeojson(null);
      setLoadingStates((prevStates) => ({ ...prevStates, [routeType]: false }));
      return;
    }

    const fetchData = async () => {
      setLoadingStates((prevStates) => ({
        ...prevStates,
        [routeType]: true,
      }));
      const params = new URLSearchParams({
        start_date: fromPicker.toISOString(),
        end_date: nowUtc().toISOString(),
        route_type: routeType,
      });
      if (vesselId != null) {
        params.set("vessel_id", String(vesselId));
      }

      const path = `/meteotracker-waypoints-geojson?${params.toString()}`;

      try {
        const data = await coreGet(
          path,
          accessContext,
          setAccessContext,
          ".coreGet meteotrackerWaypointsGeojson error=",
        );
        if (cancelled) return;

        setmeteotrackerWaypointsGeojson({
          meteotrackerWaypointsGeojson: data,
        });
        setLoadingStates((prevStates) => ({
          ...prevStates,
          [routeType]: false,
        }));
      } catch (error) {
        if (cancelled) return;
        console.error("An error occurred:", error);
        setLoadingStates((prevStates) => ({
          ...prevStates,
          [routeType]: false,
        }));
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [fromPicker, routeType, refreshTick, vesselId, enabled]);

  return {
    meteotrackerWaypointsGeojson,
    loadingState: loadingStates[routeType],
  };
};
